<template>
  <div class="list-group-item flex-column align-items-start">
    <div v-if="content.type == 'default'" class="row-wrapper">
      <div class="title" v-bind:class="{ selected: entryIsSelected }">
        {{ content.title }}
      </div>
      <div class="content">
        {{ content.name }}
      </div>
    </div>
    <div v-if="content.type == 'bands'" class="row-wrapper bands">
      <div
        class="title d-flex align-items-center"
        v-bind:class="{ selected: entryIsSelected }"
      >
        <h3>{{ content.title }}</h3>
        <Tooltip
          v-if="content.helpTxt"
          :help-text="content.helpTxt"
          help-id="header"
          :label="content.title"
        />
      </div>
      <div class="content">
        <div v-for="(b, i) in content.bands" :key="i">
          <div class="row" v-if="b.type == 'table' || b.type == 'multiline'">
            <div
              :class="sectionHeaderClass(i)"
              class="col-sm-10 d-flex justify-content-left"
            >
              <section-title :data="b" :index="i" />
            </div>
            <div class="row w-100">
              <div v-if="!b.type || b.type == ''" class="m-2 text-left">
                {{ b.text }}
              </div>
              <table-section
                v-if="b.type == 'table'"
                :data="b"
                class="col-sm-11"
              />
              <multi-line-section
                v-if="b.type == 'multiline'"
                :data="b"
                class="col-sm-12"
              />
            </div>
          </div>
          <div class="row" v-else :class="sectionHeaderClass(i)">
            <div class="col-sm d-flex justify-content-start">
              <section-title :data="b" :index="i" />
            </div>
            <div v-if="!b.type || b.type == ''" class="col-sm-10 text-left">
              {{ b.text }}
            </div>
            <!-- Single Link -->
            <div
              v-if="b.type == 'link'"
              class="col-sm-10 d-flex justify-content-start"
            >
              <div class="d-flex flex-column align-items-start">
                <div>
                  <link-wrapper v-if="b.link" :link="b.link" :name="b.text" />
                  <span v-else class="text-left">{{ b.text }}</span>
                </div>
                <div class="mb-2" v-if="b.buttons">
                  <div v-for="(btn, i) in b.buttons" :key="i">
                    <a :href="btn.url" class="btn btn-info ml-2">{{
                      btn.name
                    }}</a>
                    <a v-if="btn.helpTxt" :href="btn.link" target="_blank">
                      {{ btn.helpTxt }}
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <!-- Multiple Links (Inside Rows) -->
            <div
              v-if="b.type == 'links'"
              class="col-sm-10 d-flex justify-content-start"
            >
              <div
                v-for="(name_link, i) in b.links"
                class="d-flex flex-column align-items-start"
                :key="i"
              >
                <div class="m-2">
                  <link-wrapper
                    v-if="name_link.link"
                    :link="name_link.link"
                    :name="name_link.name"
                  />
                </div>
              </div>
            </div>
            <!-- Multiple Links (Comma Seperated) -->
            <div v-if="b.type == 'links_commasep'" class="col-sm-10">
              <div
                class="m-2"
                style="display: flex; flex-wrap: wrap; align-items: center"
              >
                <span
                  v-for="(name_link, i) in b.items"
                  :key="i"
                  style="white-space: nowrap; margin-right: 5px"
                >
                  <a
                    v-if="name_link.link"
                    :href="name_link.link"
                    rel="noopener noreferrer"
                    style="
                      white-space: nowrap;
                      overflow: hidden;
                      text-overflow: ellipsis;
                    "
                    >{{ name_link.name }}</a
                  >
                  <span v-if="i < b.items.length - 1">, </span>
                </span>
              </div>
            </div>
            <!-- IFrame -->
            <div
              v-if="b.type == 'iframe'"
              class="col-sm-10 d-flex flex-column justify-content-start"
            >
              <!-- JBrowse 2 -->
              <div v-if="b.jbrowse2Url" class="mb-3">
                <div class="d-flex justify-content-end align-items-start">
                  <a @click.prevent="reloadIFrame2" class="m-2" href="#"
                    >Center on {{ b.name }}</a
                  >
                  <span>|</span>
                  <a
                    :href="b.fullScreenUrl2"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="m-2"
                    >Full-screen view</a
                  >
                </div>
                <div class="jbrowse2-iframe-wrapper" style="height: 400px; border: 1px solid black;">
                  <iframe
                    ref="jbrowse2Iframe"
                    :title="'JBrowse2 for ' + b.name"
                    name="jbrowse2"
                    :data-src="b.jbrowse2Url"
                    tabindex="-1"
                    class="jbrowse2-iframe jbrowse2-loading"
                    style="width: 100%; height: 100%; border: none;"
                  ></iframe>
                </div>
              </div>
            </div>
            <!-- html -->
            <div
              v-if="b.type == 'html'"
              class="col-sm-10 d-flex justify-content-start"
            >
              <div class="m-2 text-left" v-html="b.html"></div>
            </div>
            <!-- Link List -->
            <div
              v-if="b.type == 'link_list'"
              class="col-sm-10 d-flex flex-row flex-wrap align-items-start"
            >
              <div v-for="(l, i) in b.items" class="mr-2" :key="i">
                <link-wrapper v-if="l.link" :link="l.link" :name="l.text" />
              </div>
            </div>
            <!-- Small Table -->
            <table-section
              v-if="b.type == 'small-table'"
              :data="b"
              class="col-sm-10"
            />
          </div>

          <!-- Multiple Links (Full) -->
          <div
            v-if="b.type == 'links'"
            class="col-sm-10 d-flex flex-column justify-content-start"
          >
            <div style="max-height: 400px; overflow: auto">
              <b-card-group
                v-for="(list_obj, i) in b.items"
                :key="i"
                style="width: 100%"
              >
                <b-card
                  :header="list_obj.title"
                  class="text-left font-weight-bold"
                >
                  <b-list-group>
                    <b-list-group-item
                      v-for="(link_obj, j) in list_obj.links"
                      :key="j"
                      class="text-left font-weight-normal"
                    >
                      <a :href="link_obj.link" target="_blank">{{
                        link_obj.name
                      }}</a></b-list-group-item
                    >
                  </b-list-group>
                </b-card>
              </b-card-group>
            </div>
          </div>
          <!-- String List -->
          <div
            v-if="b.type == 'string_list'"
            class="col-sm-10 d-flex flex-column justify-items-start"
          >
            <div
              v-for="(l, i) in b.items"
              class="mr-2 d-flex justify-content-start"
              :key="i"
            >
              {{ l }}
            </div>
          </div>
          <!-- Segment List: one line per item, each a list of { text, link? } -->
          <div
            v-if="b.type == 'segment_list'"
            class="col-sm-10 d-flex flex-column justify-items-start"
          >
            <div v-for="(line, i) in b.items" class="mr-2 text-left" :key="i">
              <template v-for="(s, j) in line">
                <a v-if="s.link" :href="s.link" :key="j">{{ s.text }}</a>
                <span v-else :key="j">{{ s.text }}</span>
              </template>
            </div>
          </div>
          <!-- Image List -->
          <div
            v-if="b.type == 'img_list'"
            class="col-sm-10 d-flex justify-content-start col-wrapper"
          >
            <div class="m-2" v-for="(list_obj, i) in b.items" :key="i">
              <a
                v-if="list_obj.img_url"
                :href="list_obj.full_url"
                target="_blank"
                class="pg-link"
                ><img class="mr-2" :src="list_obj.img_url" height="100px"
              /></a>
            </div>
          </div>
          <!-- Homologs -->
          <div
            v-if="b.type == 'homologs'"
            class="col-sm-10 d-flex justify-content-start mt-2"
          >
            <HomologsSection
              :content="b.content"
              :treeId="content.meta.treeId"
            />
          </div>
          <!-- Panther Tree Viewer -->
          <div
            v-if="b.type == 'panther_tree_viewer'"
            class="col-sm-10 d-flex justify-content-start"
          >
            <div class="mr-2">
              <img
                src="@/assets/PantherTree.png"
                height="100px"
                alt="Panther Tree"
              />
            </div>
            <div class="mr-2 pg-link">
              <a :href="b.pgLink" target="_blank"
                ><img
                  src="@/assets/phylogeneslogo.png"
                  height="100px"
                  alt="Phylogenes logo"
              /></a>
            </div>
            <div class="pg-title">View complete family and tree</div>
          </div>
          <!-- Image -->
          <image-selector v-if="b.type == 'image'" :band="b" />
          <!-- AlphaFold 3D Structure -->
          <div
            v-if="b.type == 'alphafold'"
            class="col-sm-10 d-flex flex-column justify-content-start"
          >
            <AlphaFoldViewer :uniprotId="b.uniprotId" />
          </div>
          <!-- Comment List -->
          <comments v-if="b.type == 'comment_list'" :b="b" />
          <!-- Sequences -->
          <div
            v-if="b.type == 'sequence'"
            class="col-sm-10 d-flex flex-column justify-content-start"
          >
            <div>
              <div class="d-flex flex-column justify-content-start">
                <span
                  v-for="(line, index) in b.chunks"
                  :key="index"
                  style="
                    text-align: left;
                    font-family: 'Courier New', monospace;
                    font-weight: bold;
                  "
                >
                  {{ line }}
                </span>
              </div>
              <b-button
                class="ml-2 mt-2"
                size="md"
                @click="copyToClipboard(b.chunks)"
                >Copy Sequence</b-button
              >
              <b-button class="ml-2 mt-2" size="md" @click="b.openLink"
                >Send to BLAST</b-button
              >
              <div
                v-if="showSuccessAlert"
                class="alert alert-success mt-2"
                role="alert"
              >
                Sequence copied to clipboard!
              </div>
              <div
                v-if="showErrorAlert"
                class="alert alert-danger mt-2"
                role="alert"
              >
                Failed to copy the sequence.
              </div>
            </div>
          </div>

          <hr v-if="i < content.bands.length - 1" />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import MultiLineSection from "../common/MultiLineSection.vue";
import TableSection from "../common/TableSection.vue";
import LinkWrapper from "../common/LinkWrapper.vue";
import HomologsSection from "./HomologsSection.vue";
import SectionTitle from "./SectionTitle.vue";
import ImageSelector from "./ImageSelector.vue";
import Comments from "./Comments.vue";
import AlphaFoldViewer from "./AlphaFoldViewer.vue";
import Tooltip from "@/components/common/Tooltip.vue";

// How long, after a JBrowse iframe loads, to keep reasserting the user's scroll
// position. JBrowse focuses itself a beat after load and the browser scrolls the
// frame into view; this window has to outlast that. See holdScrollPosition().
const SCROLL_HOLD_MS = 1500;

export default {
  name: "BaseEntry",
  props: ["content", "selectedColIdx", "currIdx"],
  components: {
    MultiLineSection,
    TableSection,
    LinkWrapper,
    HomologsSection,
    SectionTitle,
    ImageSelector,
    Comments,
    AlphaFoldViewer,
    Tooltip,
  },
  data() {
    return {
      selectedImgType: "",
      loading: false,
      errorImg: false,
      entryIsSelected: false,
      sortKey: "",
      showSuccessAlert: false,
      showErrorAlert: false,
    };
  },
  mounted() {
    this.entryIsSelected = false;
    this.$nextTick(() => {
      this.initIframes();
    });
  },
  beforeDestroy() {
    if (this._iframeObservers) {
      this._iframeObservers.forEach((o) => o.disconnect());
    }
    if (this._scrollHold) {
      cancelAnimationFrame(this._scrollHold.raf);
      this._scrollHold = null;
    }
  },
  computed: {},
  watch: {
    selectedColIdx: function (newVal, oldVal) {
      if (newVal == this.currIdx) {
        this.entryIsSelected = true;
      } else {
        this.entryIsSelected = false;
      }
    },
  },
  methods: {
    initIframes() {
      const iframes = this.$refs.jbrowse2Iframe;
      if (!iframes) return;
      const iframeList = Array.isArray(iframes) ? iframes : [iframes];

      iframeList.forEach((iframe) => {
        if (!iframe.getAttribute('data-src')) return;
        // Defer loading JBrowse until the iframe nears view, with a 100px
        // preload. rootMargin only expands the observer's root, so we root on
        // the iframe's own scroll container (the pane it scrolls within) rather
        // than the viewport -- otherwise that container clips the target before
        // the margin applies and the preload is lost.
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                observer.disconnect();
                this.loadJBrowse(iframe);
              }
            });
          },
          { root: this.scrollableAncestors(iframe)[0] || null, rootMargin: '100px' }
        );
        observer.observe(iframe);
        if (!this._iframeObservers) this._iframeObservers = [];
        this._iframeObservers.push(observer);
      });
    },
    // Point an iframe at its JBrowse URL, holding the user's scroll position
    // across the load. As JBrowse initializes inside its cross-origin frame it
    // focuses itself, and the browser reacts by scrolling that frame into view,
    // pulling the page off whatever the user was reading. We can't stop a
    // cross-origin frame from taking focus, and the scroll it triggers fires no
    // scroll event, so instead we remember where the user was and reassert it
    // while JBrowse settles.
    loadJBrowse(iframe) {
      const url = iframe.getAttribute('data-src');
      if (!url) return;
      // The scroll steal happens as JBrowse initializes, a beat *after* the
      // frame's load event -- so we anchor the hold to load, not to the moment
      // we set src. That also means we capture the user's settled position
      // rather than fighting a scroll they're still making to reach the section.
      iframe.addEventListener(
        'load',
        () => {
          iframe.classList.remove('jbrowse2-loading');
          this.holdScrollPosition(iframe);
        },
        { once: true }
      );
      iframe.src = url;
    },
    reloadIFrame2() {
      const iframe = Array.isArray(this.$refs.jbrowse2Iframe)
        ? this.$refs.jbrowse2Iframe[0]
        : this.$refs.jbrowse2Iframe;
      iframe.classList.add('jbrowse2-loading');
      iframe.src = '';
      this.$nextTick(() => {
        this.loadJBrowse(iframe);
      });
    },
    // The iframe's scrollable ancestors, nearest first, found by scanning up so
    // nothing is hard-coded to a specific container.
    scrollableAncestors(el) {
      const ancestors = [];
      for (let node = el.parentElement; node; node = node.parentElement) {
        const overflowY = getComputedStyle(node).overflowY;
        if (
          (overflowY === 'auto' || overflowY === 'scroll') &&
          node.scrollHeight > node.clientHeight
        ) {
          ancestors.push(node);
        }
      }
      return ancestors;
    },
    // Those ancestors plus the window, each with its current scroll offset.
    captureScrollPositions(iframe) {
      const targets = [...this.scrollableAncestors(iframe), window];
      return targets.map((target) => ({
        target,
        top: target === window ? window.scrollY : target.scrollTop,
        left: target === window ? window.scrollX : target.scrollLeft,
      }));
    },
    // Called from the iframe's load handler. Reassert the user's scroll position
    // for a short window so JBrowse's focus-driven scroll can't move the page.
    // The scroll it triggers fires no scroll event, so we can't listen for it;
    // instead we reassert per frame until the window elapses.
    holdScrollPosition(iframe) {
      // Coalesce iframes within this section: while a hold is active a later
      // iframe just extends its window instead of starting a second loop, so
      // they share one captured target rather than fighting or capturing a
      // mid-jump position. (The hold is per BaseEntry instance. Every current
      // detail page renders only one JBrowse section, so this is a page-wide
      // hold in practice; if a page ever hosted two iframe sections at once,
      // promote this to a shared/module-level hold to coalesce across them.)
      if (this._scrollHold) {
        this._scrollHold.until = performance.now() + SCROLL_HOLD_MS;
        return;
      }
      const positions = this.captureScrollPositions(iframe);
      const hold = { until: performance.now() + SCROLL_HOLD_MS, raf: 0 };
      this._scrollHold = hold;
      const tick = () => {
        positions.forEach((p) => {
          if (p.target === window) {
            if (window.scrollX !== p.left || window.scrollY !== p.top) {
              window.scrollTo(p.left, p.top);
            }
          } else {
            if (p.target.scrollLeft !== p.left) p.target.scrollLeft = p.left;
            if (p.target.scrollTop !== p.top) p.target.scrollTop = p.top;
          }
        });
        if (performance.now() < hold.until) {
          hold.raf = requestAnimationFrame(tick);
        } else {
          this._scrollHold = null;
        }
      };
      tick();
    },
    getStringFields(fields) {
      if (!fields) return [];
      return fields.filter((field) => field.cellType === "string");
    },
    getHtmlRows(fields) {
      if (!fields) return [];
      return fields.filter((field) => field.cellType === "html");
    },
    displayedComments(b) {
      if (b.showRecent) {
        return b.items.slice(-2); // Last two items
      } else {
        return b.items; // All items
      }
    },
    sectionHeaderClass(index) {
      return {
        "mr-2": true,
        "bg-white": index % 2 === 0,
        "bg-light": index % 2 !== 0,
      };
    },
    copyToClipboard(chunks) {
      const sequenceText = chunks
        .map((line) => {
          return line.substring(line.indexOf(" ") + 1).replace(/\s+/g, "");
        })
        .join("");
      navigator.clipboard
        .writeText(sequenceText)
        .then(() => {
          this.showAlert("success");
        })
        .catch((err) => {
          this.showAlert("error");
          console.error("Failed to copy the sequence: ", err);
        });
    },
    showAlert(type) {
      if (type === "success") {
        this.showSuccessAlert = true;
        setTimeout(() => (this.showSuccessAlert = false), 3000); // Hide after 3 seconds
      } else if (type === "error") {
        this.showErrorAlert = true;
        setTimeout(() => (this.showErrorAlert = false), 3000); // Hide after 3 seconds
      }
    },
  },
};
</script>
<style scoped lang="scss">
.jbrowse2-iframe-wrapper {
  position: relative;
  background: #f5f5f5;
}
.jbrowse2-loading {
  visibility: hidden;
}
.text-left {
  text-align: left;
}
.column {
  flex: 0 0 200px;
}
.title {
  display: flex;
  width: 98%;
  align-items: flex-start;
  /* based on 'entryIsSelected', change background color*/
  background-color: #f5f7f5;
  margin: 10px;
  padding-left: 10px;
  font-weight: bold;
  font-size: large;
}
.selected {
  background-color: #a6b542;
}
.col-wrapper {
  overflow-x: scroll;
}
.ellipsis {
  overflow: hidden;
  display: inline-block;
  width: 200px;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-decoration: underline;
}
.text-overflow {
  width: 100%; /* Adjust this as needed */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.text-truncate {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.authors {
  font-size: small;
  text-align: left;
}
.truncate {
  text-overflow: ellipsis;
}
.pg-link {
  cursor: pointer;
  height: 110px;
}
.pg-link:hover {
  background: #feffbb;
}
.pg-title {
  font-size: 20px;
  display: flex;
  justify-content: center;
  align-self: center;
}
.img-wrapper {
  height: 400px;
  width: 100%;
  background-color: blue;
  position: relative;
  overflow-x: auto;
  overflow-y: auto;
  border: 1px solid black;
}
.img-wrapper:hover {
  border: 2px solid black;
  cursor: grab;
}

.img-wrapper > img {
  position: relative;
}
.row-wrapper {
  border: black 1px solid;
}
.content {
  margin: 10px;
  min-height: 20px;
  padding-left: 20px;
}
.sorted-asc::after {
  content: "▲";
}

.sorted-desc::after {
  content: "▼";
}
</style>
